import { Injectable, inject, OnDestroy } from '@angular/core';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { ReplaySubject, concatMap } from 'rxjs';

import { AnalyticsConfig } from './analytics.config';
import {
  ButtonClickEvent,
  LinkClickEvent,
  PageViewEvent,
  UserTraits,
} from './analytics.models';
import Avo from './avo/Avo';
import { AnalyticsAction } from './analytics-action.type';
import { SegmentService } from './segment/segment.service';
import { AvoService } from './avo/avo.service';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService implements OnDestroy {
  private readonly segmentService = inject(SegmentService);
  private readonly avoService = inject(AvoService);

  private readonly actionQueue$ = new ReplaySubject<AnalyticsAction>();
  private analytics: AnalyticsBrowser | null = null;
  private config: AnalyticsConfig | null = null;

  constructor() {
    this.avoService
      .whenReady()
      .pipe(concatMap(() => this.actionQueue$))
      .subscribe((action) => {
        action(Avo);
      });
  }

  ngOnDestroy(): void {
    this.actionQueue$.complete();
  }

  async initialize(config: AnalyticsConfig): Promise<void> {
    if (this.isInitialized() || !config.isTrackingEnabled) {
      return;
    }

    try {
      await this.segmentService.initialize(config);

      this.segmentService
        .whenAnalyticsBrowserInitialized()
        .subscribe(async (analyticsBrowser) => {
          this.analytics = analyticsBrowser;
          await this.avoService.initialize(config, analyticsBrowser);
        });

      // Track session replay initialization event via Avo
      if (config.isSessionReplayEnabled) {
        this.segmentService.whenSessionReplayInitialized().subscribe(() => {
          this.trackSessionReplayInitialized();
        });
      }

      this.config = config;
    } catch (error) {
      this.actionQueue$.complete();
      throw error;
    }
  }

  dispatch(action: AnalyticsAction): void {
    if (!this.config?.isTrackingEnabled) {
      return;
    }

    this.actionQueue$.next(action);
  }

  trackButtonClick(event: ButtonClickEvent): void {
    this.dispatch((avo) =>
      avo.clickedButton({
        userId_: '',
        buttonName: event.buttonName,
        buttonId: event.buttonId,
        buttonType: event.buttonType,
        time: new Date().toISOString(),
        mortgageEstPurchasePrice: 0,
        experimentId: null,
        pageName: null,
        leadTypeCode: null,
      }),
    );
  }

  trackLinkClick(event: LinkClickEvent): void {
    this.dispatch((avo) =>
      avo.clickedLink({
        userId_: '',
        linkName: event.linkName,
        linkId: event.linkId,
        linkValue: event.linkValue,
        linkType: event.linkType ?? null,
        time: new Date().toISOString(),
        userRvid: '',
        learningCenterRelatedArticlePosition: null,
        interestRateType: null,
        pageName: null,
      }),
    );
  }

  trackPageView(event: PageViewEvent): void {
    this.dispatch((avo) =>
      avo.loadedPage({
        userId_: '',
        customDestinationPageName_: event.pageName,
        pageName: event.pageName,
        rocketCompany: Avo.RocketCompany.CLOSE,
        funnelExperience: Avo.FunnelExperience.CLEARSIGN,
        experimentSource: null,
        qlsCode: null,
        interestRateFinal: null,
        interestRateSuggested: null,
        interestRateRecommended: null,
        amplitudeSessionReplayId: null,
        experimentId: null,
        userRvid: null,
        leadTypeCode: null,
      }),
    );
  }

  trackSessionReplayInitialized(): void {
    this.dispatch((avo) => avo.initializedSessionReplay());
  }

  identifyUser(userId: string, traits?: UserTraits): void {
    if (!this.isInitialized() || !this.config?.isTrackingEnabled) {
      return;
    }

    this.segmentService.identify(userId, traits);
  }

  async resetSession(): Promise<void> {
    if (!this.isInitialized()) {
      return;
    }

    await this.segmentService.resetSession();
  }

  reset(): void {
    if (this.analytics) {
      this.analytics.reset();
    }
  }

  isInitialized(): boolean {
    return this.config !== null;
  }
}
